/**-------------------------------  Copyright ---------------------------------
This software has been developed by Orange Labs TGI/OLS/SOFT
Copyright (c) Orange Labs 2018

COPYRIGHT    : This file is the property of Orange Labs.
Copyright Orange Labs 2018, All Rights Reserved

This software is the confidential and proprietary information of Orange Labs.

You shall not disclose such Confidential Information and shall use it only
in accordance with the terms of the license agreement you entered into with
Orange Labs.

AUTHORS      : Orange Labs  TGI/OLS/SOFT
EMAIL        : soft.cybersec@orange.com
-----------------------------------------------------------------------------*/


import {Component, Prop, Vue} from 'vue-property-decorator'
import {StreamVO} from '@/valueObjects/StreamVO'
import AnalysisServiceMixin from '@/services/AnalysisServiceMixin'
import {AnalysisService} from '@/services/AnalysisService'
import {PacketVO} from '@/valueObjects/PacketVO'
import Download from '@/utils/Download'

const TOOLTIP_CONTENT_SIZE: number = 96
const CHARACTER_PER_LINE = 16

@Component({
    mixins: [AnalysisServiceMixin],
})
export default class AnalysisNetworkStreams extends Vue {
    @Prop()
    private hostIp: string

    private analysisService: AnalysisService
    private streams: StreamVO[] = []

    public async mounted() {
        if (this.$route.params.analysisId !== undefined) {
            await this.loadDestinationHostActivity(Number(this.$route.params.analysisId), this.hostIp)
        }
    }

    private async loadDestinationHostActivity(analysisId: number, hostIp: string): Promise<void> {
        try {
            this.streams = await this.analysisService.getHostActivity(analysisId, hostIp)
        } catch (e) {
            let errorMessage = `Failed to load host ${hostIp} activity`
            errorMessage += ` for analysisId ${analysisId}: \n${e.errorMessage}`
            this.$emit('streamsException', errorMessage)
        }
    }

    private packetWidth(packetSize: number, chunkSize: number): string {
        return (packetSize / chunkSize) * 80 + '%'
    }

    private isReceivedPacket(packet: PacketVO): boolean {
        return packet.direction === 'in'
    }

    private downloadPacket(packet: PacketVO, index: number, stream: StreamVO) {
        const filename = `${this.hostIp}-payload-${packet.direction}-stream-${stream.protocol}-${stream.port}-${index}`
        const decodedPayload = window.atob(packet.payload)
        const payloadAsArrayBuffer = this.stringToUint8ArrayBuffer(decodedPayload)
        const blob = new Blob([ payloadAsArrayBuffer ], {type: 'application/octet-stream'})
        Download.makeDownloadLink(blob, filename)
    }

    private stringToUint8ArrayBuffer(payloadAsString: string) {
        const payloadLength = payloadAsString.length
        const buffer = new ArrayBuffer(payloadLength) // 1 byte per char
        const bufferView = new Uint8Array(buffer)
        for (let i = 0; i < payloadLength; i++) {
            bufferView[i] = payloadAsString.charCodeAt(i)
        }
        return bufferView
    }

    private getPopoverContent(packet: PacketVO): [string[], string[]] {
        if (packet.payloadAsTextArray !== undefined && packet.payloadAsHexaCodes !== undefined) {
            return [packet.payloadAsHexaCodes, packet.payloadAsTextArray]
        }
        const payloadAsText = window.atob(packet.payload).substring(0, TOOLTIP_CONTENT_SIZE)
        packet.payloadAsHexaCodes = this.hexaStringToCodes(payloadAsText)

        packet.payloadAsTextArray = []
        const rawTextArray = this.toStringArray(payloadAsText)
        for (const rawText of rawTextArray) {
            const encodedText = this.encodeString(rawText)
            packet.payloadAsTextArray.push(encodedText)
        }

        return [packet.payloadAsHexaCodes, packet.payloadAsTextArray]
    }

    private encodeString(rawString: string): string {
        let result = ''
        for (const char of rawString) {
            if ((char >= 'a' && char <= 'z') || (char >= 'A' && char <= 'Z') || (char >= '0' && char <= '9') ||
                '-/\\:_+-*.,;#?=)([]{}%@!|$'.indexOf(char) >= 0) {
                result += char
            } else if (char === '<') {
                result += '&lt;'
            } else if (char === '>') {
                result += '&gt;'
            } else if (char === '"') {
                result += '&quot;'
            } else if (char === '\'') {
                result += '&apos;'
            } else if (char === '&') {
                result += '&amp;'
            } else {
                result += '&nbsp;'
            }
        }

        return result
    }

    private toStringArray(stringToSplit: string): string[] {
        const result = []
        while (stringToSplit.length > 0) {
            result.push(stringToSplit.slice(0, CHARACTER_PER_LINE))
            stringToSplit = stringToSplit.substring(CHARACTER_PER_LINE)
        }
        return result
    }

    private hexaStringToCodes(hexaData: string, limit: number = TOOLTIP_CONTENT_SIZE): string[] {
        const hexaCodes = []
        let formattedHexaValue = ''

        for (let i = 0; i < hexaData.length && i < limit; i++) {
            const utf16FirstByteValue = hexaData.charCodeAt(i)
            if (utf16FirstByteValue < 16) {
                formattedHexaValue = '0' + utf16FirstByteValue.toString(16).toUpperCase()
            } else {
                formattedHexaValue = utf16FirstByteValue.toString(16).toUpperCase()
            }
            hexaCodes.push(formattedHexaValue)
        }
        return hexaCodes
    }

    private onPacketHover(packet: PacketVO) {
        packet.isHovered = true
    }

    private onPacketOut(packet: PacketVO) {
        packet.isHovered = false
    }
}
